import React, { useCallback, useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Activity } from "lucide-react";
import BiometricVault from "@/components/longevity/BiometricVault";

/**
 * Staff → Customer → Longevity tab.
 *
 * Feb 2026 — reworked to the longitudinal Biometric Vault model.
 * Every save creates a new timestamped record. Blood Results are
 * editable here (staff-only). Biological Age is auto-calculated by
 * the backend on each save.
 *
 * Any logged-in staff account can access this tab — the Longevity
 * whitelist applies to guests only.
 */
export default function LongevityTab({ cid, customer }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/longevity/customers/${cid}/biometric`);
      setEntries(r.data?.entries || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const handleSave = useCallback(
    async (payload) => {
      try {
        await staffApi.post(`/longevity/customers/${cid}/biometric`, payload);
        await load();
      } catch (e) {
        throw new Error(formatApiError(e));
      }
    },
    [cid, load],
  );

  return (
    <div className="space-y-5" data-testid="longevity-tab-root">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2" data-testid="longevity-tab-heading">
          <Activity className="w-5 h-5 text-primary" /> Longevity Biometric Vault
        </h2>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          Append-only history for {customer?.full_name || customer?.first_name || "this guest"}.
          Each save creates a brand-new record — corrections are a new entry, not an edit.
          Blood Results are editable here only; the guest can view them read-only in their portal.
        </p>
      </div>

      <BiometricVault
        entries={entries}
        loading={loading}
        onSave={handleSave}
        mode="staff"
      />
    </div>
  );
}
